/**
 * 微信登录状态轮询工具
 * 由于微信回调到服务端，前端需要轮询检查登录状态
 */

import { useUserStore } from '@/stores/userStore';

export class WechatLoginPolling {
  constructor() {
    this.pollingInterval = null;
    this.sessionKey = null;
    this.maxPollingTime = 5 * 60 * 1000; // 5分钟超时
    this.pollingFrequency = 1000; // 1秒轮询一次，提高响应速度
    this.startTime = null;
    this.onLoginSuccess = null;
    this.onLoginFailed = null;
    this.onTimeout = null;
  }

  /**
   * 开始轮询微信登录状态
   * @param {string} sessionKey 会话标识
   * @param {Object} callbacks 回调函数
   */
  startPolling(sessionKey, callbacks = {}) {
    this.sessionKey = sessionKey;
    this.onLoginSuccess = callbacks.onSuccess;
    this.onLoginFailed = callbacks.onFailed;
    this.onTimeout = callbacks.onTimeout;
    this.startTime = Date.now();

    console.log('开始轮询微信登录状态:', sessionKey);

    // 清除可能存在的旧轮询
    this.stopPolling();

    // 立即检查一次状态
    this.checkLoginStatus();

    // 开始新的轮询
    this.pollingInterval = setInterval(() => {
      this.checkLoginStatus();
    }, this.pollingFrequency);

    // 设置超时
    setTimeout(() => {
      if (this.pollingInterval) {
        this.stopPolling();
        console.log('微信登录轮询超时');
        this.onTimeout?.('登录超时，请重试');
      }
    }, this.maxPollingTime);
  }

  /**
   * 检查登录状态
   */
  async checkLoginStatus() {
    try {
      // 使用后端实际提供的API
      const { checkWechatLoginStatus } = await import('@/services/wechatService');
      const result = await checkWechatLoginStatus(this.sessionKey);

      if (result.success && result.code === 0) {
        switch (result.data.status) {
          case 'confirmed': {
            // 登录成功 - 根据后端文档，成功状态是'confirmed'
            console.log('微信登录成功:', result.data);
            this.stopPolling();
            
            // 构造用户数据对象
            const userData = {
              userId: result.data.userId || result.data.user_id,
              username: result.data.username,
              nickname: result.data.nickname,  // ✅ 添加nickname字段
              avatar: result.data.avatar,
              roles: result.data.roles,
              token: result.data.token,
              refreshToken: result.data.refreshToken
            };
            
            // 设置用户数据到store
            if (userData.token) {
              const userStore = useUserStore();
              userStore.setUserData(userData, true);
            }
            
            this.onLoginSuccess?.(userData);
            break;
          }
            
          case 'expired':
            // 二维码过期
            console.log('微信二维码已过期');
            this.stopPolling();
            this.onLoginFailed?.('二维码已过期，请重新扫码');
            break;
            
          case 'pending':
            // 继续等待
            console.log('微信登录等待中...');
            break;
            
          default:
            console.log('未知登录状态:', result.data.status);
        }
      } else {
        console.error('检查登录状态失败:', result);
      }
    } catch (error) {
      console.error('轮询微信登录状态出错:', error);
      // 不立即停止轮询，可能是网络临时问题
    }
  }

  /**
   * 停止轮询
   */
  stopPolling() {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  /**
   * 获取轮询状态
   */
  getPollingInfo() {
    return {
      isPolling: !!this.pollingInterval,
      sessionKey: this.sessionKey,
      elapsedTime: this.startTime ? Date.now() - this.startTime : 0,
      remainingTime: this.maxPollingTime - (this.startTime ? Date.now() - this.startTime : 0)
    };
  }
}

/**
 * 创建微信登录轮询实例
 */
export function createWechatPolling() {
  return new WechatLoginPolling();
}

/**
 * 全局轮询实例
 */
let globalPolling = null;

/**
 * 获取全局轮询实例
 */
export function getGlobalWechatPolling() {
  if (!globalPolling) {
    globalPolling = new WechatLoginPolling();
  }
  return globalPolling;
}

export default WechatLoginPolling; 